// Upload into Document Control from the browser.
//
// Document Control could only ever RECORD a link that something else had
// already uploaded — POST /doc-control takes a `link` and never sees a
// file — so there was no way to file a PDF from the web app at all. This
// posts the file itself.
//
// TWO MODES, because a delivered drawing set is not a document:
//
//   DOCUMENTS (POST /doc-control/upload) — one file or many, of any kind
//   the project documentation needs: PDFs and images (every page
//   rendered), DWG/DXF drawings, Revit/IFC/Navisworks models, Excel and
//   CSV schedules, P6/MS Project files, Word specifications, ZIP
//   deliveries (helpers/docTypes). One file is named here; several are
//   named after their files. Streamed to disk, up to 1 GB each.
//
//   DRAWING SET (POST /doc-control/import-set) — one PDF of N sheets,
//   split into N Doc Control rows, each with the number, title and
//   discipline read off its own title block. Nothing to type: the sheets
//   already say what they are. Streamed to disk, so the ceiling is GBs
//   rather than MB, and the work runs in the worker — this modal watches
//   the job rather than the upload.
import { TradeWithPartners } from "components/Partners/PartnerTradeLabel";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import React, { useEffect, useRef, useState } from "react";
import {
  Modal, Upload, Input, Select, Button, message, Alert, Radio, Progress, Table, Tag, Tooltip,
} from "antd";
import { InboxOutlined, FileTextOutlined, PartitionOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { T } from "pages/Issue/theme";
import { DOC_ACCEPT, KIND_LABEL, MAX_DOC_MB, docKindOf } from "helpers/docTypes";
import { matchOption } from "components/SearchSelect/searchSelect";

const MAX_MB = MAX_DOC_MB;   // each document — streamed to disk server-side
const MAX_SET_MB = 2048;     // a set — streamed straight to disk
const POLL_MS = 2000;

export default function UploadDocumentModal({
  open, onClose, projects = [], projectId, disciplines = [], onUploaded,
}) {
  const [mode, setMode] = useState("one");
  const [file, setFile] = useState(null);
  // Several documents at once: each is posted in turn and reported on its own.
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  // What the project already holds, checked before any bytes are sent:
  // key -> { status: new | filed | revision, sheetName, revisionNumber }.
  const [checks, setChecks] = useState({});
  const [checking, setChecking] = useState(false);
  const [project, setProject] = useState(projectId || undefined);
  const tradeIx = usePartnerTrades(project);
  const [discipline, setDiscipline] = useState(undefined);
  const [sheetName, setSheetName] = useState("");
  const [sheetNumber, setSheetNumber] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  // Set-import job being watched.
  const [job, setJob] = useState(null);
  const poll = useRef(null);

  const isSet = mode === "set";
  const projectTitle = ((projects || []).find((p) => String(p._id) === String(project)) || {}).title || "";

  const stopPolling = () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };
  useEffect(() => stopPolling, []);

  // Reset only when the window OPENS. Keyed on props as well, it wiped the
  // picked files whenever the caller re-rendered with a new projects array
  // or the disciplines arrived late (owner report, 2026-09-17).
  const openProps = useRef({ projectId, disciplines });
  openProps.current = { projectId, disciplines };
  useEffect(() => {
    if (!open) { stopPolling(); return; }
    setFile(null); setFiles([]); setResults([]); setChecks({}); setSheetName(""); setSheetNumber(""); setDescription("");
    setProgress(0); setJob(null); setBusy(false); setMode("one");
    setProject(openProps.current.projectId || undefined);
    setDiscipline(openProps.current.disciplines?.[0]?._id);
  }, [open]);
  // Disciplines that arrive after opening fill an empty choice, nothing more.
  useEffect(() => {
    if (open && !discipline && disciplines && disciplines.length) setDiscipline(disciplines[0]._id);
  }, [open, disciplines, discipline]);

  const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified || 0}`;

  // antd calls beforeUpload once PER FILE with the whole selection as
  // `list`. The batch is taken once, on its first file: one state update
  // for 30 files, not 30 nested updates each re-rendering the list.
  const takeFile = (f, list) => {
    if (!f) return Upload.LIST_IGNORE;
    const batch = Array.isArray(list) && list.length ? list : [f];
    if (batch[0] !== f) return Upload.LIST_IGNORE;
    if (isSet) {
      if (!/\.pdf$/i.test(f.name || "")) { message.error("A drawing set has to be a PDF"); return Upload.LIST_IGNORE; }
      if (f.size > MAX_SET_MB * 1024 * 1024) { message.error(`That file is ${(f.size / 1024 / 1024).toFixed(0)} MB — the limit is ${MAX_SET_MB} MB`); return Upload.LIST_IGNORE; }
      setFile(f);
      return Upload.LIST_IGNORE;
    }
    const refused = [];
    const accepted = batch.filter((x) => {
      if (!docKindOf(x.name)) { refused.push(`${x.name} (type not taken)`); return false; }
      if (x.size > MAX_MB * 1024 * 1024) { refused.push(`${x.name} (over ${MAX_MB} MB)`); return false; }
      return true;
    });
    if (refused.length) message.error(`Not added: ${refused.slice(0, 5).join(", ")}${refused.length > 5 ? ` and ${refused.length - 5} more` : ""}`, 6);
    // A finished batch is done: picking again starts a new list, so nothing
    // already filed is sent twice.
    const base = results.length ? [] : files;
    if (results.length) setResults([]);
    const seen = new Set(base.map(fileKey));
    const next = [...base, ...accepted.filter((x) => { const k = fileKey(x); if (seen.has(k)) return false; seen.add(k); return true; })];
    setFiles(next);
    setFile(next[0] || null);
    if (next.length === 1) setSheetName(String(next[0].name || "").replace(/\.[a-z0-9]+$/i, ""));
    checkAgainstProject(next, results.length ? {} : checks);
    return Upload.LIST_IGNORE;  // never auto-upload, and antd keeps no list of its own
  };
  // sha1 in the browser for files up to 256 MB (the size of a big DWG set);
  // a bigger model is compared by size, which is enough to say "same file".
  const sha1Of = async (f) => {
    try {
      if (!window.crypto || !window.crypto.subtle || f.size > 256 * 1024 * 1024) return "";
      const buf = await f.arrayBuffer();
      const digest = await window.crypto.subtle.digest("SHA-1", buf);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch (e) { return ""; }
  };
  const checkAgainstProject = async (list, known) => {
    const todo = list.filter((f) => !known[fileKey(f)]);
    if (!project || !todo.length) return;
    setChecking(true);
    try {
      const payload = [];
      for (const f of todo) {
        // eslint-disable-next-line no-await-in-loop
        payload.push({ name: f.name, size: f.size, hash: await sha1Of(f) });
      }
      const r = await fetch(`${url}/doc-control/upload/check`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: auth() },
        body: JSON.stringify({ projectId: project, files: payload }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && Array.isArray(body.files)) {
        setChecks((prev) => {
          const next = { ...prev };
          body.files.forEach((c, i) => { next[fileKey(todo[i])] = c; });
          return next;
        });
      }
    } catch (e) { /* the upload itself still refuses duplicates */ }
    setChecking(false);
  };
  const toSend = files.filter((f) => (checks[fileKey(f)] || {}).status !== "filed");
  // A different project answers differently: check the picked files again.
  const checkedFor = useRef(project);
  useEffect(() => {
    if (!open || checkedFor.current === project) return;
    checkedFor.current = project;
    setChecks({});
    if (files.length) checkAgainstProject(files, {});
  }, [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const removeFile = (key) => {
    const next = files.filter((x) => fileKey(x) !== key);
    setFiles(next);
    setFile(next[0] || null);
  };

  const auth = () => `Bearer ${localStorage.getItem(tokenRef)}`;

  // Watch the split. The upload finishes in seconds; the reading of a
  // few hundred title blocks does not.
  const watch = (importId) => {
    stopPolling();
    poll.current = setInterval(async () => {
      try {
        const res = await fetch(`${url}/doc-control/import-set/${importId}`, { headers: { Authorization: auth() } });
        const body = await res.json();
        if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
        setJob(body);
        if (body.status === "done" || body.status === "failed") {
          stopPolling();
          setBusy(false);
          if (body.status === "done") {
            const s = body.stats || {};
            message.success(`${s.filed || 0} sheets filed from ${body.fileName}`);
            onUploaded && onUploaded(null);
          } else {
            message.error(body.error || "The import failed");
          }
        }
      } catch (e) {
        stopPolling();
        setBusy(false);
        message.error(e.message || "Lost track of the import");
      }
    }, POLL_MS);
  };

  // Several documents: one POST each, in order, so one refusal does not
  // cost the rest and the progress bar means something.
  const uploadOne = (f, index, batchId, last) => new Promise((resolve) => {
    const fd = new FormData();
    fd.append("projectId", project);
    // The server notifies the project once for the whole batch.
    fd.append("batchId", batchId);
    if (last) fd.append("batchLast", "1");
    if (discipline) fd.append("discipline", discipline);
    fd.append("sheetName", String(f.name || "").replace(/\.[a-z0-9]+$/i, ""));
    if (description.trim()) fd.append("description", description.trim());
    fd.append("file", f);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${url}/doc-control/upload`);
    xhr.setRequestHeader("Authorization", auth());
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round(((index + e.loaded / e.total) / Math.max(1, toSend.length)) * 100));
    };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || "{}"); } catch (e) { /* keep {} */ }
      resolve(xhr.status >= 200 && xhr.status < 300
        ? { name: f.name, ok: true, revised: !!body.revised, revisionNumber: body.revisionNumber, kind: body.fileKind, pages: body.pageCount || 0, warning: body.warning || "", doc: body.data }
        : { name: f.name, ok: false, duplicate: !!body.duplicate, error: body.err || `HTTP ${xhr.status}` });
    };
    xhr.onerror = () => resolve({ name: f.name, ok: false, error: "Connection failed" });
    xhr.send(fd);
  });

  const submitMany = async () => {
    setBusy(true); setProgress(0); setResults([]);
    const batchId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const out = files.filter((f) => !toSend.includes(f)).map((f) => ({ name: f.name, key: fileKey(f), ok: false, duplicate: true, skipped: true, error: "already filed, not sent" }));
    for (let i = 0; i < toSend.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const r = await uploadOne(toSend[i], i, batchId, i === toSend.length - 1);
      out.push({ ...r, key: fileKey(toSend[i]) });
      setResults([...out]);
    }
    setBusy(false);
    const ok = out.filter((r) => r.ok).length;
    const failed = out.filter((r) => !r.ok && !r.duplicate).length;
    if (ok) { message.success(`${ok} document${ok === 1 ? "" : "s"} filed`); onUploaded && onUploaded(null); }
    if (failed) message.error(`${failed} could not be filed; see the list`);
    // The batch is finished: the list now shows what happened, and the next
    // pick starts a new batch (takeFile) instead of re-sending these.
    setFiles([]);
    setFile(null);
  };

  const submit = () => {
    if (!isSet && (files.length > 1 || (files.length === 1 && toSend.length === 0))) {
      if (!project) return message.warning("Pick the project these belong to");
      return submitMany();
    }
    if (!file) return message.warning("Choose a file");
    if (!project) return message.warning("Pick the project this belongs to");
    if (!isSet && !sheetName.trim()) return message.warning("Give the document a name");

    const fd = new FormData();
    // Fields before the file: multer reads them as the stream arrives.
    fd.append("projectId", project);
    if (!isSet) {
      if (discipline) fd.append("discipline", discipline);
      fd.append("sheetName", sheetName.trim());
      fd.append("sheetNumber", sheetNumber.trim());
      fd.append("description", description.trim());
    }
    fd.append("file", file);

    // XHR rather than fetch: a multi-hundred-MB set on site broadband
    // needs a progress bar, and fetch cannot report upload progress.
    setBusy(true);
    setProgress(0);
    setJob(null);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${url}/doc-control/${isSet ? "import-set" : "upload"}`);
    xhr.setRequestHeader("Authorization", auth());
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || "{}"); } catch (e) { /* keep {} */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (isSet) {
          // Uploaded, not finished — the split has only just been queued.
          setJob({ status: "queued", pageCount: body.pageCount, pagesDone: 0, fileName: file.name });
          watch(body.importId);
          return;
        }
        setBusy(false);
        const n = body.pageCount || 0;
        message.success(n > 1 ? `Uploaded — ${n} sheets` : `${KIND_LABEL[body.fileKind] || "Document"} uploaded`);
        if (body.warning) message.warning(body.warning, 6);
        onUploaded && onUploaded(body.data);
      } else {
        setBusy(false);
        message.error(body.err || `Upload failed (HTTP ${xhr.status})`);
      }
    };
    xhr.onerror = () => { setBusy(false); message.error("Upload failed — check the connection and try again"); };
    xhr.send(fd);
  };

  const stats = (job && job.stats) || {};
  const done = job && job.status === "done";

  return (
    <Modal
      open={open}
      onCancel={busy ? undefined : onClose}
      title={isSet ? "Import a drawing set" : "Upload documents"}
      width={done ? 760 : 560}
      destroyOnClose
      maskClosable={!busy}
      closable={!busy}
      footer={done || (!isSet && !busy && !files.length && results.length > 0) ? [<Button key="d" type="primary" onClick={onClose}>Done</Button>] : [
        <Button key="c" onClick={onClose} disabled={busy}>Cancel</Button>,
        <Button key="u" type="primary" loading={busy || checking} onClick={submit} disabled={isSet ? !file : (!files.length || checking || !toSend.length)}>
          {busy ? (isSet ? "Importing…" : `Uploading ${progress}%`)
            : checking ? "Checking what is already filed…"
              : !isSet && files.length && !toSend.length ? "All already filed"
                : `${isSet ? "Import set" : toSend.length > 1 ? `Upload ${toSend.length} documents` : "Upload"}${projectTitle ? ` to ${projectTitle}` : ""}`}
        </Button>,
      ]}
    >
      {!job && (
        <Radio.Group
          value={mode}
          onChange={(e) => { setMode(e.target.value); setFile(null); setFiles([]); setResults([]); }}
          disabled={busy}
          style={{ marginBottom: 14, width: "100%" }}
          buttonStyle="solid"
        >
          <Radio.Button value="one" style={{ width: "50%", textAlign: "center" }}>
            <FileTextOutlined /> Documents
          </Radio.Button>
          <Radio.Button value="set" style={{ width: "50%", textAlign: "center" }}>
            <PartitionOutlined /> Drawing set
          </Radio.Button>
        </Radio.Group>
      )}

      {!job && (
        // The project comes FIRST and is named on the button: it defaults to
        // the last project used, and a batch of another job's documents
        // dropped under the wrong project is a mess to undo.
        <div style={{ marginBottom: 12, padding: "8px 10px", border: `1px solid ${project ? T.navy : "#d9d9d9"}`, borderRadius: 4 }}>
          <div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Filing into project</div>
            <Select filterOption={matchOption}
              style={{ width: "100%" }} value={project} onChange={setProject}
              placeholder="Which project is this for?" disabled={busy}
              showSearch optionFilterProp="label"
              options={projects.map((p) => ({ value: String(p._id), label: p.title }))}
            />
          </div>
        </div>
      )}

      {!job && (
        <Upload.Dragger
          multiple={!isSet}
          maxCount={isSet ? 1 : undefined}
          beforeUpload={takeFile}
          // Our own list below, keyed by name+size+date: a controlled antd
          // list with index uids re-rendered and re-keyed every item on every
          // pick, which is what froze the tab on a second batch.
          showUploadList={false}
          accept={isSet ? ".pdf" : DOC_ACCEPT}
          disabled={busy}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 6 }}>
            <InboxOutlined style={{ color: T.navy }} />
          </p>
          <p style={{ fontSize: 14, color: T.ink, margin: 0 }}>
            {isSet ? "Drop the whole set here, or click to choose" : "Drop documents here, or click to choose"}
          </p>
          <p style={{ fontSize: 12, color: T.muted, margin: "6px 0 0" }}>
            {isSet
              ? `PDF only, up to ${MAX_SET_MB} MB. Every sheet becomes its own document.`
              : `PDF, images, DWG/DXF, Revit/IFC/Navisworks models, Excel/CSV, P6/MS Project, Word and ZIP, up to ${MAX_MB} MB each. Every page of a PDF is rendered, so it can be paged through and marked up.`}
          </p>
        </Upload.Dragger>
      )}

      {!job && isSet && file && (
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          {file.name} <span style={{ color: T.muted }}>({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
          {!busy && <Button size="small" type="link" onClick={() => setFile(null)}>Remove</Button>}
        </div>
      )}

      {!job && !isSet && (files.length > 0 || results.length > 0) && (
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 4, marginBottom: 12 }}>
          {(files.length ? files : []).map((f) => {
            const key = fileKey(f);
            const r = results.find((x) => x.key === key);
            return (
              <div key={key} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 8px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{f.name}</span>
                <span style={{ color: T.muted, whiteSpace: "nowrap" }}>{KIND_LABEL[docKindOf(f.name)] || "file"}, {(f.size / 1024 / 1024).toFixed(1)} MB</span>
                {r ? <Tag color={r.ok ? "green" : r.duplicate ? "default" : "red"} style={{ margin: 0 }}>{r.ok ? (r.revised ? `revision ${r.revisionNumber}` : "filed") : r.duplicate ? "already filed" : "failed"}</Tag>
                  : (checks[key] && checks[key].status !== "new")
                    ? <Tag color={checks[key].status === "filed" ? "default" : "blue"} style={{ margin: 0 }} title={checks[key].sheetName}>{checks[key].status === "filed" ? "already filed, skipped" : `new revision of rev ${checks[key].revisionNumber}`}</Tag>
                    : null}
                {!r && !busy && <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={() => removeFile(key)}>Remove</Button>}
              </div>
            );
          })}
          {!files.length && results.map((r) => (
            <div key={r.key} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 8px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5 }}>
              <span style={{ flex: 1, minWidth: 0 }} title={r.name}>{r.name}{!r.ok ? <span style={{ color: "#b91c1c" }}> {r.error}</span> : r.warning ? <span style={{ color: T.muted }}> {r.warning}</span> : null}</span>
              <Tag color={r.ok ? "green" : r.duplicate ? "default" : "red"} style={{ margin: 0 }}>{r.ok ? (r.revised ? `revision ${r.revisionNumber}` : "filed") : r.duplicate ? "already filed" : "failed"}</Tag>
            </div>
          ))}
        </div>
      )}
      {!job && !isSet && !files.length && results.length > 0 && (
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
          <b>Batch finished:</b> {results.filter((r) => r.ok && !r.revised).length} filed, {results.filter((r) => r.revised).length} new revisions, {results.filter((r) => r.duplicate).length} already filed, {results.filter((r) => !r.ok && !r.duplicate).length} failed. Drop more files to start a new batch, or press Done.
        </div>
      )}

      {!job && (
        <div style={{ display: "grid", gap: 10 }}>


          {/* A set names its own sheets — asking for a title and a
              discipline here would be asking for one answer to cover
              two hundred different sheets. */}
          {!isSet && files.length > 1 && (
            <div style={{ fontSize: 12, color: T.muted }}>
              {files.length} documents: each is named after its file. The discipline and description below apply to all of them.
            </div>
          )}
          {!isSet && (
            <>
              {files.length <= 1 && <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Document name</div>
                  <Input value={sheetName} onChange={(e) => setSheetName(e.target.value)}
                         placeholder="e.g. Level 2 mechanical" disabled={busy} />
                </div>
                <div style={{ width: 150 }}>
                  <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Sheet number</div>
                  <Input value={sheetNumber} onChange={(e) => setSheetNumber(e.target.value)}
                         placeholder="M-201" disabled={busy} />
                </div>
              </div>}

              <div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Discipline</div>
                <Select filterOption={matchOption}
                  style={{ width: "100%" }} value={discipline} onChange={setDiscipline}
                  placeholder="Discipline" disabled={busy}
                  showSearch optionFilterProp="text" optionLabelProp="name"
                  // Each discipline with the partners who carry it on this
                  // project beneath (owner, 2026-10-16).
                  options={(disciplines || []).map((d) => {
                    const partners = tradeIx.partnersOf(d.key, d.value);
                    return { value: String(d._id), name: d.value || d.key, text: [d.value, d.key, ...partners].filter(Boolean).join(" "), label: <TradeWithPartners label={d.value || d.key} partners={partners} /> };
                  })}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Description (optional)</div>
                <Input.TextArea rows={2} value={description} disabled={busy}
                                onChange={(e) => setDescription(e.target.value)} />
              </div>
            </>
          )}
        </div>
      )}

      {!job && isSet && (
        <Alert
          style={{ marginTop: 14 }}
          type="info"
          showIcon
          icon={<PartitionOutlined />}
          message="Each sheet is filed on its own"
          description="The set is split page by page, and every sheet's number, title and discipline are read off its title block. Sheets whose title block cannot be read are still filed — under the file name and their page number — so nothing is lost."
        />
      )}

      {!job && !isSet && files.length === 1 && file && /\.pdf$/i.test(file.name || "") && (
        <Alert
          style={{ marginTop: 14 }}
          type="info"
          showIcon
          icon={<FileTextOutlined />}
          message="Is this a whole drawing set?"
          description="Uploaded this way it stays one document. Switch to 'Drawing set' above to split it into one row per sheet, each named and filed by discipline automatically."
        />
      )}

      {/* ---------- watching the split ---------- */}
      {job && (
        <div>
          <div style={{ fontSize: 13, color: T.ink, marginBottom: 8 }}>
            {job.fileName}
            {job.pageCount ? <span style={{ color: T.muted }}> · {job.pageCount} sheets</span> : null}
          </div>
          <Progress
            percent={job.pageCount ? Math.round(((job.pagesDone || 0) / job.pageCount) * 100) : 0}
            status={job.status === "failed" ? "exception" : (done ? "success" : "active")}
          />
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
            {job.status === "failed"
              ? (job.error || "The import failed")
              : done
                ? `${stats.filed || 0} filed · ${stats.withNumber || 0} numbered · ${stats.withDiscipline || 0} with a discipline${stats.failed ? ` · ${stats.failed} could not be filed` : ""}`
                : (job.stage || "starting") + "…"}
          </div>

          {done && Array.isArray(job.sheets) && (
            <Table
              size="small"
              rowKey={(r) => r.page}
              dataSource={job.sheets}
              pagination={job.sheets.length > 12 ? { pageSize: 12, size: "small" } : false}
              scroll={{ y: 320 }}
              columns={[
                { title: "Pg", dataIndex: "page", width: 52 },
                {
                  title: "Sheet",
                  dataIndex: "sheetNumber",
                  width: 110,
                  render: (v) => v || <span style={{ color: T.muted }}>—</span>,
                },
                {
                  title: "Title",
                  dataIndex: "title",
                  render: (v, r) => v || <span style={{ color: T.muted }}>{r.error || "not read"}</span>,
                },
                {
                  title: "Discipline",
                  dataIndex: "discipline",
                  width: 120,
                  render: (v, r) => (r.disciplineId
                    ? v
                    : <Tooltip title="No matching discipline on this company — file it by hand"><span style={{ color: T.muted }}>{v || "—"}</span></Tooltip>),
                },
                {
                  title: "Read",
                  dataIndex: "readBy",
                  width: 96,
                  render: (v, r) => (
                    <Tag color={r.confidence === "high" ? "green" : r.confidence === "medium" ? "blue" : "default"}>
                      {v === "title-block" ? "title block" : v === "claude" ? "automatic" : v || "—"}
                    </Tag>
                  ),
                },
              ]}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
